import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

const Header = () => {
  const { handleButtonClick } = useButtonLinks();
  const { language, toggleLanguage, isArabic } = useLanguage();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Force re-render when language changes
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [language]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [logos, setLogos] = useState({
    main: '/assets/logos/RiverPrimeLogo(1).png',
    favicon: '/assets/logos/Fav.png'
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Load admin logos from localStorage
    const loadAdminLogos = () => {
      const adminLogos = localStorage.getItem('adminLogos');
      console.log('Loading admin logos:', adminLogos);
      
      if (adminLogos) {
        try {
          const parsedLogos = JSON.parse(adminLogos);
          console.log('Parsed logos:', parsedLogos);
          
          setLogos({
            main: parsedLogos.main || '/assets/logos/RiverPrimeLogo(1).png',
            favicon: parsedLogos.favicon || '/assets/logos/Fav.png'
          });
          
          // Update favicon if uploaded
          if (parsedLogos.favicon && parsedLogos.favicon !== '/favicon.ico') {
            console.log('Updating favicon to:', parsedLogos.favicon);
            const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = parsedLogos.favicon;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
        } catch (error) {
          console.error('Error parsing admin logos:', error);
        }
      } else {
        console.log('No admin logos found in localStorage');
      }
    };
    
    loadAdminLogos();
    
    // Listen for localStorage changes (when admin uploads new logos)
    const handleStorageChange = () => {
      loadAdminLogos();
    };
    
    // Listen for custom logo update events (for same-page updates)
    const handleLogoUpdate = () => {
      loadAdminLogos();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('logoUpdated', handleLogoUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('logoUpdated', handleLogoUpdate);
      clearInterval(timer);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDropdownToggle = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const navigateToSupport = () => {
    // Navigate to support page and scroll to top
    window.location.href = '/support';
    window.scrollTo(0, 0);
    
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <header className="header">
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-brand">
            <Link to="/" className="logo-link" onClick={scrollToTop}>
              <div className="logo-container">
                {logos.main && logos.main !== '/assets/logos/logo-fallback.svg' ? (
                  <img src={logos.main} alt="RiverPrime" className="brand-logo" onError={(e) => {
                    console.log('Logo failed to load:', logos.main);
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }} />
                ) : null}
                <div className="text-logo" style={{ 
                  display: (!logos.main || logos.main === '/assets/logos/logo-fallback.svg') ? 'block' : 'none',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#ffffff'
                }}>
                  <span style={{ color: '#ffffff' }}>River</span>
                  <span style={{ color: '#ffdd00' }}>Prime</span>
                </div>
              </div>
            </Link>
          </div>
          
          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <Link to="/" className="nav-link" onClick={scrollToTop}>
              {t('nav.home')}
            </Link>
            <Link to="/platforms" className="nav-link" onClick={scrollToTop}>
              {t('nav.platforms')}
            </Link>
            <div className="nav-dropdown" key={`accounts-${language}`}>
              <button 
                className="nav-link dropdown-toggle" 
                onClick={() => handleDropdownToggle('accounts')}
              >
                {t('nav.accounts')}
                <i className={`fas fa-chevron-down ${activeDropdown === 'accounts' ? 'rotated' : ''}`}></i>
              </button>
              <div className={`dropdown-menu ${activeDropdown === 'accounts' ? 'active' : ''}`}>
                <Link to="/accounts/pro" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'حساب المحترف' : 'Pro Account'}
                </Link>
                <Link to="/accounts/prime" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'الحساب المميز' : 'Prime Account'}
                </Link>
                <Link to="/accounts/premium" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'حساب الخبير' : 'Premium Account'}
                </Link>
              </div>
            </div>
            <div className="nav-dropdown" key={`markets-${language}`}>
              <button 
                className="nav-link dropdown-toggle" 
                onClick={() => handleDropdownToggle('markets')}
              >
                {t('nav.markets')}
                <i className={`fas fa-chevron-down ${activeDropdown === 'markets' ? 'rotated' : ''}`}></i>
              </button>
              <div className={`dropdown-menu ${activeDropdown === 'markets' ? 'active' : ''}`}>
                <Link to="/markets/forex" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'العملات الأجنبية' : 'Forex'}
                </Link>
                <Link to="/markets/indices" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'المؤشرات' : 'Indices'}
                </Link>
                <Link to="/markets/commodities" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'السلع' : 'Commodities'}
                </Link>
                <Link to="/markets/shares" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'الأسهم' : 'Shares'}
                </Link>
              </div>
            </div>
            <Link to="/partnership" className="nav-link" onClick={scrollToTop}>
              {t('nav.partnership')}
            </Link>
            <div className="nav-dropdown" key={`about-${language}`}>
              <button 
                className="nav-link dropdown-toggle" 
                onClick={() => handleDropdownToggle('about')}
              >
                {t('nav.aboutUs')}
                <i className={`fas fa-chevron-down ${activeDropdown === 'about' ? 'rotated' : ''}`}></i>
              </button>
              <div className={`dropdown-menu ${activeDropdown === 'about' ? 'active' : ''}`}>
                <Link to="/about/company-values" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'قيم الشركة' : 'Company Values'}
                </Link>
                <Link to="/about/why-river-prime" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'لماذا ريفر برايم' : 'Why River Prime'}
                </Link>
                <Link to="/about/regulations" className="dropdown-item" onClick={scrollToTop}>
                  {language === 'ar' ? 'التنظيم والترخيص' : 'Regulations'}
                </Link>
                <button className="dropdown-item" onClick={navigateToSupport}>
                  {language === 'ar' ? 'اتصل بنا' : 'Contact Us'}
                </button>
              </div>
            </div>
            <div className="nav-utilities">
              <div className="nav-language">
                <button className="language-btn language-toggle" onClick={toggleLanguage}>
                  <i className="fas fa-globe"></i>
                  <span>{t('nav.language')}</span>
                </button>
              </div>
              <div className="nav-buttons">
                <button 
                  className="nav-btn nav-btn-demo"
                  onClick={(e) => handleButtonClick('tryDemo', e)}
                >
                  <i className="fas fa-play"></i>
                  {t('nav.tryDemo')}
                </button>
                <button 
                  className="nav-btn nav-btn-primary"
                  onClick={(e) => handleButtonClick('getStarted', e)}
                >
                  <i className="fas fa-user-plus"></i>
                  {t('nav.getStarted')}
                </button>
                <button 
                  className="nav-btn nav-btn-login"
                  onClick={(e) => handleButtonClick('login', e)}
                >
                  <i className="fas fa-sign-in-alt"></i>
                  {t('nav.login')}
                </button>
              </div>
            </div>
          </div>
          
          <div className="nav-hamburger" onClick={toggleMenu}>
            <span className={`hamburger-line ${isMenuOpen ? 'active' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'active' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'active' : ''}`}></span>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;